// utils/imageResolver.js
// Centralized image URL resolution and base URL handling

// Get media base URL for CDN assets (supports environment-specific media domains)
import { isAssortedBox } from './packages';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:8000';

// Single source of truth for cards (Species Grid, Available Now, etc.)
export function resolveImageUrls(entity) {
  if (!entity) return { url: null, thumbUrl: null };

  const url =
    // 1) species cover asset (display)
    entity?.cover_display_url ??
    // 2) general display thumb (the server-side cascade)
    entity?.display_thumb_url ??
    // 3) species cover asset (thumb)
    entity?.cover_thumb_url ??
    // 4) legacy shapes we still see in the wild (DO NOT DELETE)
    entity?.cover_asset?.display_url ??
    entity?.cover_asset?.thumb_url ??
    entity?.primary_image_url ??
    entity?.image_url ??
    entity?.images?.[0]?.url ??
    null;

  const thumbUrl =
    entity?.cover_thumb_url ??
    entity?.cover_asset?.thumb_url ??
    entity?.thumb_url ??
    entity?.images?.[0]?.thumb_url ??
    null;

  return { url, thumbUrl };
}

// Ordered gallery for the product detail carousel (Photo Cascade S2-01.1).
// Reads the server-provided `gallery` list ([{url, thumb_url, display_url,
// credit, credit_info}] from public_product_detail); builds absolute URLs
// and normalizes credit to a display string. #110e: creditInfo (structured
// {source, source_license, source_attribution}) rides along so the
// carousel can render a license pill for CC assets whose attribution TEXT
// is empty — string-only credit dropped those. Hero/carousel slides prefer
// the 1200px display rendition (display_url falls back to url server-side).
// Falls back to the single species hero so the carousel always has at
// least one slide. Returns [{ url, thumbUrl, credit, creditInfo }].
export function resolveGalleryImages(product) {
  if (!product) return [];
  const raw = Array.isArray(product.gallery) ? product.gallery : [];
  const items = raw
    .map((g) => ({
      url: buildAssetUrl(g?.display_url || g?.url),
      thumbUrl: buildAssetUrl(g?.thumb_url || g?.url),
      credit: (g?.credit || '').trim() || null,
      creditInfo: g?.credit_info || null,
    }))
    .filter((g) => g.url);
  if (items.length > 0) return items;

  // I-03 S3-01.6 — an ASSORTED box never falls back to its anchor
  // species' hero: the anchor is bookkeeping, and its photo would promise
  // a species the box does not. No cover → no image (the page shows its
  // placeholder), which is the honest answer.
  if (isAssortedBox(product)) return [];

  // Fallback: single species hero as a one-slide gallery (covers species
  // with no MediaLink rows — cover cascade still yields an image).
  const { url, thumbUrl } = resolveImageUrls(product.species);
  const hero = buildAssetUrl(url);
  if (hero) {
    const heroCredit = (product.species?.cover_credit?.source_attribution || '').trim() || null;
    return [{
      url: hero,
      thumbUrl: buildAssetUrl(thumbUrl) || hero,
      credit: heroCredit,
      creditInfo: product.species?.cover_credit || null,
    }];
  }
  return [];
}

export function buildAssetUrl(u) {
  if (!u) return null;
  // If already absolute, return as-is. If root-relative ('/media/...'), prefix with media CDN base.
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return MEDIA_BASE_URL + u;
  return `${MEDIA_BASE_URL}/${u}`; // rare case
}

// Resolve the attribution payload that accompanies the resolved image
// URL. Mirrors the cascade in resolveImageUrls — variation cover →
// morph cover → species cover. Returns the entity's `cover_credit`
// object, or null when the displayed image is a facility upload (no
// credit required) or when the field isn't present in the response.
// (P-08-Images-Phase-2 S-08i2b, 2026-05-30.)
export function resolveCredit(entity) {
  if (!entity) return null;
  return entity.cover_credit || null;
}

export function getVariationTileCredit(variation, product) {
  // Same cascade as getVariationTileImage. Whichever level resolves
  // the actual displayed image is the one whose credit applies.
  if (variation) {
    const v = resolveCredit(variation);
    // Need to also detect when variation has no image; in that case
    // we fall through. resolveImageUrls returns null url when nothing
    // is set; here we just check the entity has any cover field.
    const hasVariationImage = variation.cover_display_url || variation.cover_thumb_url || variation.cover_asset?.thumb_url;
    if (hasVariationImage) return v;
  }
  const morphName = variation?.morph?.name?.trim().toLowerCase();
  if (variation?.morph && morphName && morphName !== 'normal') {
    const morphHasImage = variation.morph.cover_display_url || variation.morph.cover_thumb_url || variation.morph.cover_asset?.thumb_url;
    if (morphHasImage) return resolveCredit(variation.morph);
  }
  return resolveCredit(product?.species || variation?.species);
}

// Context-specific image resolution functions
export function getSpeciesImage(species) {
  const { url } = resolveImageUrls(species);
  return buildAssetUrl(url);
}

// Facility Species Grid - cover asset only (no cascade fallbacks)
export function getSpeciesGridImage(species) {
  const url = species?.cover_display_url ?? species?.cover_thumb_url ?? null;
  return buildAssetUrl(url);
}

export function getProductCardImage(product) {
  // Species image only for product cards
  const { url } = resolveImageUrls(product.species);
  return buildAssetUrl(url);
}

export function getProductHeroImage(product) {
  // Species image only for hero
  const { url } = resolveImageUrls(product.species);
  return buildAssetUrl(url);
}

export function getVariationTileImage(variation, product) {
  const variationId = variation?.id || 'unknown';
  
  // Cascade variation → morph → species using resolveImageUrls on each
  const variationImg = resolveImageUrls(variation);
  if (variationImg.url) {
    // Used variation image
    return buildAssetUrl(variationImg.url);
  }

  // Skip morph image for "Normal" or missing morphs - fallback directly to species
  const morphName = variation.morph?.name?.trim().toLowerCase();
  const shouldUseMorphImage = variation.morph && morphName && morphName !== 'normal';
  
  if (shouldUseMorphImage) {
    const morphImg = resolveImageUrls(variation.morph);
    if (morphImg.url) {
      // Used morph image
      return buildAssetUrl(morphImg.url);
    }
  }

  const speciesImg = resolveImageUrls(product?.species || variation?.species);
  if (speciesImg.url) {
    // Used species image as fallback
    return buildAssetUrl(speciesImg.url);
  }

  // No image found
  return null;
}

export function getAnimalImage(animal) {
  const animalId = animal?.id || 'unknown';
  
  // Cascade: animal → morph → species
  const animalImg = resolveImageUrls(animal);
  if (animalImg.url) {
    // Used animal image
    return buildAssetUrl(animalImg.url);
  }
  
  // Try morph if animal has one
  const morphImg = resolveImageUrls(animal.genetics?.expressed_morph);
  if (morphImg.url) {
    const morphName = animal.genetics?.expressed_morph?.name || 'unknown';
    // Used morph image
    return buildAssetUrl(morphImg.url);
  }
  
  // Fall back to species
  const speciesImg = resolveImageUrls(animal.species);
  if (speciesImg.url) {
    const speciesName = `${animal.species?.genus || ''} ${animal.species?.species || ''}`.trim();
    // Used species image as fallback
    return buildAssetUrl(speciesImg.url);
  }
  
  // No image found
  return null;
}

export function getClutchImage(clutch) {
  // Clutch only - no fallback per requirements
  const clutchImg = resolveImageUrls(clutch);
  if (clutchImg.url) {
    return buildAssetUrl(clutchImg.url);
  }

  return null;
}

// Generic model image URL helper (for backward compatibility)
export function getModelImageUrl(model) {
  const { url } = resolveImageUrls(model);
  return buildAssetUrl(url);
}
// True when an <img> src already IS the no-photo tile (query ignored, so a
// cache-busted retry of the tile itself is still recognised).
export function isNoPhotoSrc(src) {
  return typeof src === 'string' && src.split('?')[0].endsWith(NO_PHOTO_SRC);
}

// Show the app's neutral no-photo tile on an <img> and mark it, so the
// image's own stylesheet can draw it whole (`[data-no-photo]`: contain on a
// pale ground) rather than cropping or stretching it. Batch 34 review: the
// old fallback was a 1x1 translucent-blue pixel, which every image box
// stretched into a purple square.
export function showNoPhoto(img) {
  if (!img) return;
  img.setAttribute('data-no-photo', '1');
  if (!isNoPhotoSrc(img.src)) img.src = NO_PHOTO_SRC;
}

// #99: one cache-busted retry before giving up. A single transient fetch
// failure (CDN blip, connection reset during the SPA-nav request burst)
// used to swap in the fallback permanently — reading as "image never loads
// until refresh". Retry once; only then the no-photo tile.
// Batch 34 verify NIT: an <img> that sets `objectFit: cover` INLINE (the
// order and checkout thumbnails) keeps covering after `showNoPhoto`, so the
// tile is cropped. Same retry rule, and the tile is drawn `contain`.
export function retryThenTile(e) {
  retryThenPlaceholder(e);
  const img = e && e.target;
  if (img && isNoPhotoSrc(img.src) && img.style) img.style.objectFit = 'contain';
}

export function retryThenPlaceholder(e) {
  const img = e.target;
  if (!img.dataset.ffRetried && img.src && !isNoPhotoSrc(img.src)) {
    img.dataset.ffRetried = '1';
    const sep = img.src.includes('?') ? '&' : '?';
    img.src = `${img.src}${sep}ffr=${Date.now()}`;
  } else if (!isNoPhotoSrc(img.src)) {
    showNoPhoto(img);
  }
}
